import { Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@core/auth/auth.service';
import { Branch } from '@core/models/branch.model';
import { OrganisationListItem } from '@core/models/organisation/organisation-list-item.model';
import { User } from '@core/models/user/user.model';
import { ONBOARDING_WELCOME_FLAG } from '@core/guard/onboarding.guard';
import { BranchService } from '@core/services/branch.service';
import { OrganisationService } from '@core/services/organisation.service';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { Subject, filter, take, takeUntil } from 'rxjs';

type WelcomeRole = 'admin' | 'manager' | 'employee';

/**
 * One-time welcome for people who never onboarded themselves: they were invited into an existing
 * organisation, so the signup wizard (which builds an organisation) never ran for them and nobody
 * ever told them what the platform does. Shown over whatever page they land on — invited members of
 * a premium organisation are sent straight to their workspace after login, never past the dashboard
 * guard that carries the wizard.
 *
 * Every shortcut opens in a new tab on purpose, so the overlay stays behind in this one. Both the
 * shortcuts and the dismiss button mark the onboarding as done.
 */
@Component({
    selector: 'app-onboarding-welcome',
    templateUrl: './onboarding-welcome.component.html',
    styleUrl: './onboarding-welcome.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OnboardingWelcomeComponent implements OnInit, OnDestroy {

  @ViewChild('dialog') dialog!:DialogComponent;

  // The workspace the menu is showing as current, so both point at the same organisation.
  @Input() currentOrganisationId:string = '';

  user!:User;
  organisation:OrganisationListItem|null = null;
  role:WelcomeRole = 'employee';
  supportEmail = 'support@smartlions.email';

  // What a personal account is good for — the same for everyone, whatever their role.
  readonly personalLines = [
    'onboarding.member.personal-1',
    'onboarding.member.personal-2',
    'onboarding.member.personal-3',
  ];

  private readonly roleLinesByRole:Record<WelcomeRole, string[]> = {
    admin: [
      'onboarding.member.admin-1',
      'onboarding.member.admin-2',
      'onboarding.member.admin-3',
      'onboarding.member.admin-4',
      'onboarding.member.admin-5',
    ],
    manager: [
      'onboarding.member.manager-1',
      'onboarding.member.manager-2',
      'onboarding.member.manager-3',
    ],
    employee: [],
  };

  private stop$ = new Subject<void>();

  constructor(
    private authService:AuthService,
    private organisationService:OrganisationService,
    private branchService:BranchService,
    private router:Router,
  ){}

  ngOnInit(): void {
    this.branchService.getBranch().pipe(
      filter((branch): branch is Branch => branch !== null),
      takeUntil(this.stop$),
    ).subscribe(branch => this.supportEmail = branch.supportEmail);

    if(!this.authService.IsLoggedIn) return;
    if(this.branchService.vanityMode) return;
    // Only greet on a first-ever login or after a long absence; the flag then keeps it dismissed for
    // the rest of this session (it is cleared again on the next login).
    if(!this.shouldWelcomeByLogin()) return;
    if(localStorage.getItem(ONBOARDING_WELCOME_FLAG) === '1') return;
    // The signup wizard lives on this route and covers the welcome itself.
    if(this.router.url.startsWith('/onboarding')) return;

    // Only greet people who were invited into an organisation; a fresh account without any is what
    // the wizard on /onboarding is for.
    this.loadOrganisation(() => {
      if(this.organisation) this.show();
    });
  }

  // Greet on a first-ever login (no previous login recorded), or when the previous login was more than
  // three months ago. lastLogin is the login before this session, so it is a stable value all session.
  private shouldWelcomeByLogin():boolean {
    const lastLogin = this.authService.User.lastLogin;
    console.log(lastLogin)
    if(!lastLogin) return true;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - 3);
    return new Date(lastLogin) < cutoff;
  }

  // Reopened from the help button in the menu, as a refresher — no skip flag involved.
  open(){
    this.loadOrganisation(() => this.show());
  }

  ngOnDestroy(): void {
    this.stop$.next();
    this.stop$.complete();
  }

  get roleLines():string[] {
    return this.roleLinesByRole[this.role];
  }

  // Re-picks on every open: the user can switch workspace between two visits to this overlay.
  private loadOrganisation(done:() => void){
    this.user = this.authService.User;

    if(this.organisationService.currentOrganisationList$.value === null){
      this.organisationService.getAll();
    }

    this.organisationService.currentOrganisationList$.pipe(
      filter((list): list is OrganisationListItem[] => list !== null),
      take(1),
      takeUntil(this.stop$),
    ).subscribe(list => {
      // This list only holds organisations the user is connected to (same source the onboarding
      // guard reads).
      this.organisation = this.pickOrganisation(list);
      this.role = this.organisation?.admin ? 'admin' : this.organisation?.manager ? 'manager' : 'employee';
      done();
    });
  }

  /**
   * The organisation the copy and the shortcuts talk about: the workspace the user is in, so people
   * connected to several of them get the one in front of them instead of an arbitrary pick. Falls
   * back to a role that has something to do here, then to the list order (premium first) — an
   * employee has no workspace to be in.
   */
  private pickOrganisation(list:OrganisationListItem[]):OrganisationListItem|null {
    return (this.currentOrganisationId ? list.find(o => o.id === this.currentOrganisationId) : undefined)
      ?? list.find(o => o.admin)
      ?? list.find(o => o.manager)
      ?? list[0]
      ?? null;
  }

  // Out of the current change detection pass: the dialog can be opened from a subscription that
  // fires while this component is still being checked.
  private show(){
    setTimeout(() => this.dialog?.show());
  }

  // Taking a shortcut counts as onboarded: the flag has to be set before the new tab opens, or that
  // tab boots without it and greets them all over again on the page they just asked for. The overlay
  // itself stays up here until it is dismissed.
  markDone(){
    localStorage.setItem(ONBOARDING_WELCOME_FLAG, '1');
  }

  close(){
    this.markDone();
    this.dialog.hide();
  }
}
